import { PHI_BOX_COLOR, PHI_BOX_FILL, DRAWING_CONFIG } from '../constants/phi'
import type { SVGOverlayWithObserver } from '../types/phi'

/**
 * Creates an SVG overlay element for PHI boxes
 */
export const createSVGOverlay = (
  viewportWidth: number,
  viewportHeight: number,
  className: string = 'phi-overlay',
  isEditMode: boolean = false,
): SVGElement => {
  const svgOverlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svgOverlay.setAttribute('class', className)
  svgOverlay.setAttribute('width', viewportWidth.toString())
  svgOverlay.setAttribute('height', viewportHeight.toString())
  svgOverlay.style.position = 'absolute'
  svgOverlay.style.top = '0'
  svgOverlay.style.left = '0'
  svgOverlay.style.width = '100%'
  svgOverlay.style.height = '100%'
  svgOverlay.setAttribute('viewBox', `0 0 ${viewportWidth} ${viewportHeight}`)
  svgOverlay.setAttribute('preserveAspectRatio', 'none')
  svgOverlay.style.pointerEvents = isEditMode ? 'auto' : 'none'

  return svgOverlay
}

/**
 * Creates a drawing overlay with grid pattern
 */
export const createDrawingOverlay = (element: HTMLElement): SVGElement => {
  // Clear any existing drawing overlays first
  const existingOverlay = element.querySelector('.drawing-overlay') as SVGElement
  if (existingOverlay) {
    existingOverlay.remove()
  }

  const viewportRect = element.getBoundingClientRect()
  const svgOverlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svgOverlay.setAttribute('class', 'drawing-overlay')
  svgOverlay.setAttribute('width', viewportRect.width.toString())
  svgOverlay.setAttribute('height', viewportRect.height.toString())
  svgOverlay.style.position = 'absolute'
  svgOverlay.style.top = '0'
  svgOverlay.style.left = '0'
  svgOverlay.style.width = '100%'
  svgOverlay.style.height = '100%'
  svgOverlay.setAttribute('viewBox', `0 0 ${viewportRect.width} ${viewportRect.height}`)
  svgOverlay.setAttribute('preserveAspectRatio', 'none')
  svgOverlay.style.pointerEvents = 'none'

  // Add grid pattern
  const gridPattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern')
  gridPattern.setAttribute('id', 'drawingGrid')
  gridPattern.setAttribute('width', DRAWING_CONFIG.gridPattern.width.toString())
  gridPattern.setAttribute('height', DRAWING_CONFIG.gridPattern.height.toString())
  gridPattern.setAttribute('patternUnits', 'userSpaceOnUse')

  const gridLine = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  gridLine.setAttribute('d', `M ${DRAWING_CONFIG.gridPattern.width} 0 L 0 0 0 ${DRAWING_CONFIG.gridPattern.height}`)
  gridLine.setAttribute('fill', 'none')
  gridLine.setAttribute('stroke', 'rgba(90, 204, 230, 0.2)')
  gridLine.setAttribute('stroke-width', DRAWING_CONFIG.gridPattern.strokeWidth.toString())

  gridPattern.appendChild(gridLine)

  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
  defs.appendChild(gridPattern)
  svgOverlay.appendChild(defs)

  const gridBackground = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
  gridBackground.setAttribute('width', '100%')
  gridBackground.setAttribute('height', '100%')
  gridBackground.setAttribute('fill', 'url(#drawingGrid)')
  svgOverlay.appendChild(gridBackground)

  element.appendChild(svgOverlay)
  return svgOverlay
}

/**
 * Creates a PHI box rectangle element
 */
export const createPHIBox = (
  sopInstanceUid: string,
  index: number,
  x: number,
  y: number,
  width: number,
  height: number,
  imageX: number,
  imageY: number,
  imageWidth: number,
  imageHeight: number,
  isSelected: boolean = false,
): SVGRectElement => {
  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
  rect.setAttribute('class', 'phi-box')
  rect.setAttribute('id', `phi-box-${sopInstanceUid}-${index}`)
  rect.setAttribute('x', x.toString())
  rect.setAttribute('y', y.toString())
  rect.setAttribute('width', width.toString())
  rect.setAttribute('height', height.toString())
  rect.setAttribute('fill', PHI_BOX_FILL)
  rect.setAttribute('stroke', isSelected ? '#ff9900' : PHI_BOX_COLOR)
  rect.setAttribute('stroke-width', isSelected ? '3' : '2')
  rect.setAttribute('data-phi-box', 'true')

  // Store original image coordinates as data attributes
  rect.setAttribute('data-image-x', imageX.toString())
  rect.setAttribute('data-image-y', imageY.toString())
  rect.setAttribute('data-image-width', imageWidth.toString())
  rect.setAttribute('data-image-height', imageHeight.toString())

  return rect
}

/**
 * Creates resize handles for a PHI box
 */
export const createResizeHandles = (
  sopInstanceUid: string,
  index: number,
  x: number,
  y: number,
  width: number,
  height: number,
): SVGRectElement[] => {
  const handleSize = DRAWING_CONFIG.handleSize
  const handles = [
    { id: 'nw', x: x, y: y },
    { id: 'n', x: x + width / 2, y: y },
    { id: 'ne', x: x + width, y: y },
    { id: 'e', x: x + width, y: y + height / 2 },
    { id: 'se', x: x + width, y: y + height },
    { id: 's', x: x + width / 2, y: y + height },
    { id: 'sw', x: x, y: y + height },
    { id: 'w', x: x, y: y + height / 2 },
  ]

  return handles.map((handle) => {
    const handleElement = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    handleElement.setAttribute('class', 'phi-box-handle')
    handleElement.setAttribute('id', `phi-box-handle-${sopInstanceUid}-${index}-${handle.id}`)
    handleElement.setAttribute('x', (handle.x - handleSize / 2).toString())
    handleElement.setAttribute('y', (handle.y - handleSize / 2).toString())
    handleElement.setAttribute('width', handleSize.toString())
    handleElement.setAttribute('height', handleSize.toString())
    handleElement.setAttribute('fill', PHI_BOX_COLOR)
    handleElement.setAttribute('stroke', '#ffffff')
    handleElement.setAttribute('stroke-width', '1')
    handleElement.setAttribute('data-handle-id', handle.id)
    handleElement.style.cursor = `${handle.id}-resize`

    return handleElement
  })
}

/**
 * Creates a drawing rectangle element
 */
export const createDrawingRect = (x: number, y: number): SVGRectElement => {
  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
  rect.setAttribute('x', x.toString())
  rect.setAttribute('y', y.toString())
  rect.setAttribute('width', '0')
  rect.setAttribute('height', '0')
  rect.setAttribute('fill', PHI_BOX_FILL)
  rect.setAttribute('stroke', PHI_BOX_COLOR)
  rect.setAttribute('stroke-width', '2')
  rect.setAttribute('stroke-dasharray', '5,5')

  return rect
}

/**
 * Finds a PHI box element by SOP instance UID and index
 */
export const findBoxById = (sopInstanceUid: string, index: number): SVGRectElement | null => {
  const boxId = `phi-box-${sopInstanceUid}-${index}`
  return document.getElementById(boxId) as SVGRectElement | null
}

/**
 * Clears all overlay elements from a container
 */
export const clearOverlays = (element: HTMLElement): void => {
  const existingOverlay = element.querySelector('.phi-overlay') as SVGElement & SVGOverlayWithObserver
  if (existingOverlay) {
    // Remove event listeners if they exist
    if (existingOverlay.mouseMoveHandler) {
      element.removeEventListener('mousemove', existingOverlay.mouseMoveHandler)
      document.removeEventListener('mousemove', existingOverlay.mouseMoveHandler)
    }

    // Clean up resize observer if it exists
    if (existingOverlay.resizeObserver) {
      existingOverlay.resizeObserver.disconnect()
    }

    existingOverlay.remove()
  }

  // Also clear drawing overlays
  const drawingOverlay = element.querySelector('.drawing-overlay') as SVGElement
  if (drawingOverlay) {
    drawingOverlay.remove()
  }
}

/**
 * Updates drawing rectangle dimensions
 */
export const updateDrawingRect = (
  rect: SVGRectElement,
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
): void => {
  const width = Math.abs(currentX - startX)
  const height = Math.abs(currentY - startY)
  const x = Math.min(startX, currentX)
  const y = Math.min(startY, currentY)

  rect.setAttribute('x', x.toString())
  rect.setAttribute('y', y.toString())
  rect.setAttribute('width', width.toString())
  rect.setAttribute('height', height.toString())
}
